import { AnimatePresence, motion } from 'framer-motion';
import { Coins, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  BottomSheet,
  Button,
  NumPad,
  SegmentedControl,
} from '../../components';
import type { Bucket503020, Category, TransactionType } from '../../data/types';
import { cn } from '../../lib/cn';
import { formatCurrency } from '../../lib/currency';
import {
  CATEGORY_COLORS,
  CATEGORY_ICON_NAMES,
  resolveIcon,
} from '../../lib/icons';
import { springGentle, springQuick, tapScale } from '../../tokens';

type Props = {
  /** 'add' for a new category, an existing Category to edit, null to close. */
  mode: 'add' | Category | null;
  /** Pre-fills type for the add flow. */
  defaultType?: TransactionType;
  /** Currency for the allocation display. */
  currency?: string;
  onClose: () => void;
  onSave: (
    input:
      | {
          mode: 'add';
          input: {
            name: string;
            type: TransactionType;
            icon: string;
            color: string;
            bucket503020?: Bucket503020;
            zeroBasedBudget?: number;
          };
        }
      | {
          mode: 'edit';
          id: string;
          patch: Partial<Category>;
        },
  ) => void;
  onDelete?: (id: string) => void;
};

const BUCKET_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'needs', label: 'Needs' },
  { value: 'wants', label: 'Wants' },
  { value: 'savings', label: 'Savings' },
] as const;

export function CategoryEditSheet({
  mode,
  defaultType = 'expense',
  currency = 'USD',
  onClose,
  onSave,
  onDelete,
}: Props) {
  const open = mode !== null;
  const editing = mode && mode !== 'add' ? mode : null;

  const [name, setName] = useState('');
  const [type, setType] = useState<TransactionType>(defaultType);
  const [iconName, setIconName] = useState(CATEGORY_ICON_NAMES[0]!);
  const [color, setColor] = useState(CATEGORY_COLORS[6]!); // blue
  const [bucket, setBucket] = useState<'' | Bucket503020>('');
  const [allocation, setAllocation] = useState<number>(0);
  const [allocationOpen, setAllocationOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setType(editing.type);
      setIconName(editing.icon);
      setColor(editing.color);
      setBucket(editing.bucket503020 ?? '');
      setAllocation(editing.zeroBasedBudget ?? 0);
    } else {
      setName('');
      setType(defaultType);
      setIconName(CATEGORY_ICON_NAMES[0]!);
      setColor(CATEGORY_COLORS[6]!);
      setBucket('');
      setAllocation(0);
    }
    setAllocationOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing?.id, defaultType]);

  const canSave = name.trim().length > 0;

  const submit = () => {
    if (!canSave) return;
    if (editing) {
      onSave({
        mode: 'edit',
        id: editing.id,
        patch: {
          name: name.trim(),
          type,
          icon: iconName,
          color,
          bucket503020:
            type === 'expense' && bucket ? (bucket as Bucket503020) : undefined,
          zeroBasedBudget:
            type === 'expense' && allocation > 0 ? allocation : undefined,
        },
      });
    } else {
      onSave({
        mode: 'add',
        input: {
          name: name.trim(),
          type,
          icon: iconName,
          color,
          bucket503020:
            type === 'expense' && bucket ? (bucket as Bucket503020) : undefined,
          zeroBasedBudget:
            type === 'expense' && allocation > 0 ? allocation : undefined,
        },
      });
    }
    onClose();
  };

  const Preview = resolveIcon(iconName);

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={editing ? 'Edit category' : 'Add category'}
    >
      <div className="px-card-pad py-4 flex flex-col gap-4">
        {/* Preview + name */}
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-button shrink-0 inline-flex items-center justify-center text-white shadow-sm"
            style={{ backgroundColor: color }}
          >
            <Preview size={22} strokeWidth={1.5} />
          </div>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Category name"
            autoCapitalize="sentences"
            maxLength={30}
            className="w-full h-12 px-4 rounded-button bg-surface-secondary text-[16px] text-text-primary placeholder:text-text-tertiary focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>

        {/* Type — only when adding new */}
        {!editing ? (
          <div>
            <span className="text-caption uppercase text-text-tertiary mb-2 block">
              Type
            </span>
            <SegmentedControl
              options={[
                { value: 'expense', label: 'Expense' },
                { value: 'income', label: 'Income' },
              ]}
              value={type}
              onChange={(v) => setType(v as TransactionType)}
            />
          </div>
        ) : null}

        {/* Color */}
        <div>
          <span className="text-caption uppercase text-text-tertiary mb-2 block">
            Color
          </span>
          <div className="grid grid-cols-6 gap-2">
            {CATEGORY_COLORS.map((c) => {
              const active = color === c;
              return (
                <motion.button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  whileTap={tapScale}
                  transition={springQuick}
                  aria-pressed={active}
                  className={cn(
                    'aspect-square rounded-pill inline-flex items-center justify-center shadow-sm',
                    active
                      ? 'ring-2 ring-text-primary ring-offset-2 ring-offset-surface'
                      : '',
                  )}
                  style={{ backgroundColor: c }}
                />
              );
            })}
          </div>
        </div>

        {/* Icon */}
        <div>
          <span className="text-caption uppercase text-text-tertiary mb-2 block">
            Icon
          </span>
          <div className="grid grid-cols-6 gap-2 max-h-[180px] overflow-y-auto pr-1">
            {CATEGORY_ICON_NAMES.map((iname) => {
              const Icon = resolveIcon(iname);
              const active = iconName === iname;
              return (
                <motion.button
                  key={iname}
                  type="button"
                  onClick={() => setIconName(iname)}
                  whileTap={tapScale}
                  transition={springQuick}
                  aria-label={iname}
                  aria-pressed={active}
                  className={cn(
                    'aspect-square rounded-button inline-flex items-center justify-center',
                    active
                      ? 'bg-accent text-white'
                      : 'bg-surface-secondary text-text-secondary',
                  )}
                >
                  <Icon size={18} strokeWidth={1.5} />
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Budget tags — always shown for expense categories so users can
            tag categories ahead of switching models. Income categories
            don't use these fields. */}
        {type === 'expense' ? (
          <div className="flex flex-col gap-3">
            <span className="text-caption uppercase text-text-tertiary block">
              Budget tags
            </span>
            <div>
              <span className="text-[12px] text-text-secondary mb-2 block">
                50 / 30 / 20 bucket
              </span>
              <SegmentedControl
                options={[...BUCKET_OPTIONS]}
                value={bucket}
                onChange={(v) => setBucket(v as '' | Bucket503020)}
              />
            </div>

            {/* Monthly allocation — tap-to-expand inline NumPad. Staged
                in local state and committed when the main Save fires. */}
            <div className="rounded-button bg-surface-secondary overflow-hidden">
              <motion.button
                type="button"
                onClick={() => setAllocationOpen((v) => !v)}
                whileTap={tapScale}
                transition={springQuick}
                aria-expanded={allocationOpen}
                className="w-full flex items-center justify-between px-4 py-3 text-left min-h-tap"
              >
                <span className="flex items-center gap-2 text-[13px] text-text-primary">
                  <Coins size={14} strokeWidth={1.8} className="text-text-tertiary" />
                  Monthly allocation
                </span>
                <span
                  className={cn(
                    'text-[14px] font-mono tabular-nums',
                    allocation > 0 ? 'text-accent' : 'text-text-tertiary',
                  )}
                >
                  {allocation > 0
                    ? formatCurrency(allocation, { currency })
                    : 'Set'}
                </span>
              </motion.button>

              <AnimatePresence initial={false}>
                {allocationOpen ? (
                  <motion.div
                    key="alloc"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={springGentle}
                    style={{ overflow: 'hidden' }}
                  >
                    <NumPad
                      key={`alloc-${editing?.id ?? 'new'}`}
                      initialValue={allocation}
                      onDone={(v) => {
                        setAllocation(v);
                        setAllocationOpen(false);
                      }}
                      doneLabel="Apply"
                      caption={null}
                    />
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
            <p className="text-[11px] text-text-tertiary -mt-1 px-1">
              Tags only show up where the model uses them: Needs/Wants/Savings
              for 50/30/20, Monthly allocation for Zero-Based.
            </p>
          </div>
        ) : null}

        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!canSave}
          onClick={submit}
        >
          {editing ? 'Update' : 'Save'}
        </Button>

        {editing && onDelete ? (
          <Button
            variant="destructive"
            fullWidth
            leadingIcon={<Trash2 size={16} strokeWidth={1.5} />}
            onClick={() => {
              onDelete(editing.id);
              onClose();
            }}
          >
            Delete category
          </Button>
        ) : null}
      </div>
    </BottomSheet>
  );
}
